import {
  ProductFeedbackFeature,
  useSubmitProductFeedback,
} from "@/hooks/use-submit-product-feedback";
import { FeedbackDialog, FeedbackDialogProps } from "@faro-lotv/flat-ui";
import { useCallback, type JSX } from "react";
import { FeedbackDialogBaseProps } from "../feedback-types";

export type FloorPlanGenerationFeedbackDialogProps = FeedbackDialogBaseProps &
  Pick<FeedbackDialogProps, "numberedLabels"> & {
    /** The main question to show in the dialog. */
    question: string;

    /** Order ID to attach to the feedback */
    orderId?: number;
  };

/**
 * @returns A feedback dialog that sends feedback to the floor plan generation feedback channel
 */
export function FloorPlanGenerationFeedbackDialog({
  question,
  numberedLabels,
  orderId,
  onClose,
}: FloorPlanGenerationFeedbackDialogProps): JSX.Element | null {
  const submitProductFeedback = useSubmitProductFeedback();

  const onSubmitFeedback = useCallback(
    async (
      rating: number,
      feedbackText?: string,
      hasUserAllowedContact?: boolean,
    ) => {
      if (
        await submitProductFeedback({
          feature: ProductFeedbackFeature.floorPlanGeneration,
          question,
          rating,
          additionalInfo: `Order ID: ${orderId ?? "n/a"}`,
          message: feedbackText,
          hasUserAllowedContact,
        })
      ) {
        onClose(true);
      }
    },
    [onClose, orderId, question, submitProductFeedback],
  );

  const onCancel = useCallback(() => {
    onClose(false);
  }, [onClose]);

  return (
    <FeedbackDialog
      isDialogOpen
      type="numbered"
      numberedLabels={numberedLabels}
      onSubmitFeedback={onSubmitFeedback}
      title={question}
      feedbackQuestionText="Additional comments (optional)"
      onClose={onCancel}
      hideQuestionUntilRating
      showXButton
    />
  );
}
